import React, {useEffect, useState} from 'react';
import {Alert, Button, Form} from 'react-bootstrap';
import {getP2pExecutors, getP2pField, changeP2pExecution, p2pErrorMessage} from '../api/p2p';

const phases = {
  local: 'NVR locale selezionato',
  switching: 'Passaggio al P2P in corso',
  p2p_active: 'P2P attivo sul campo',
  p2p_waiting_worker: 'P2P selezionato: in attesa del servizio di registrazione',
  returning_local: 'Ritorno all’NVR locale in corso'
};

export default function AdminP2pExecution({fieldId, token, saved, onChanged}) {
  const [agents, setAgents] = useState([]);
  const [worker, setWorker] = useState('');
  const [status, setStatus] = useState(saved.execution);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [stale, setStale] = useState(false);
  useEffect(() => { setStatus(saved.execution); setStale(false); }, [saved]);
  useEffect(() => {
    let disposed = false;
    const refresh = async () => {
      try {
        const [list, configuration] = await Promise.all([getP2pExecutors(token), getP2pField(fieldId, token)]);
        if (disposed) return;
        setAgents(list.agents.filter(a => a.fieldIds.includes(fieldId)));
        setStatus(configuration.execution);
        // Preserve unsaved form edits; a concurrent change requires a fresh form.
        setStale(configuration.revision !== saved.revision);
        setError('');
      } catch (err) { if (!disposed) setError(p2pErrorMessage(err)); }
    };
    refresh();
    const timer = setInterval(refresh, 10000);
    return () => { disposed = true; clearInterval(timer); };
  }, [fieldId, token, saved.revision]);

  const change = async owner => {
    setBusy(true); setError('');
    try {
      const configuration = await changeP2pExecution(fieldId, {
        owner, revision: saved.revision, generation: saved.execution.generation,
        workerId: owner === 'p2p' ? worker : null
      }, token);
      onChanged(configuration);
    } catch (err) { setError(p2pErrorMessage(err)); }
    finally { setBusy(false); }
  };
  const selected = agents.find(a => a.id === worker && a.ready);
  const prepared = selected && (selected.preparedFieldIds || []).includes(fieldId);
  const p2p = status && status.owner === 'p2p';
  const media = p2p && status.media;
  const gib = value => (value / (1024 ** 3)).toFixed(1);
  const operationLabel = {segment: 'Preparazione e consegna della diretta', sd: 'Recupero dalla scheda SD', final: 'Preparazione e consegna del video completo'};
  const mediaWarnings = {
    recovery_no_progress: 'Il recupero è in attesa di verifica: tre tentativi non hanno fornito i fotogrammi mancanti. I file disponibili sono conservati.',
    sd_interval_unavailable: 'La scheda SD non contiene un intervallo richiesto. Verifica il programma di registrazione della telecamera: i tratti non registrati non possono essere recuperati.',
    sd_validation_paused: 'Il recupero SD è temporaneamente sospeso per una verifica. La diretta e i file già ricevuti restano disponibili.',
    source_clock_alignment_required: 'Prima di unire diretta e SD serve confermare che i fotogrammi corrispondano. Il video completo resta in attesa.',
    source_clock_alignment_inconsistent: 'Gli orari di diretta e SD non sono coerenti. Il video completo resta in attesa di verifica.',
    camera_clock_discontinuity: 'È stata rilevata una variazione dell’orologio della telecamera. Il video completo resta in attesa di verifica.'
  };
  return <div className="p2p-execution-panel mt-3" aria-label="Servizio di registrazione">
    <h6>Servizio di registrazione</h6>
    <p role="status">{status && phases[status.phase]}</p>
    {(saved.enabled || p2p) && <Alert variant="info">Servizio in collaudo. I video vengono consegnati al player Replayer e allo storage. Il recupero degli intervalli mancanti richiede che siano stati registrati sulla SD. Le copie temporanee vengono rimosse dopo la conferma di consegna.</Alert>}
    {media && <section aria-label="Coda video e spazio">
      <h6>Coda video e spazio</h6>
      {media.stale ? <Alert variant="warning">Stato del server non aggiornato. Ultimo aggiornamento: {new Date(media.updatedAt).toLocaleTimeString()}.</Alert>
        : <p>{media.recording ? `Registrazione in corso — slot ${media.slotId}` : 'Nessuna registrazione in corso'}</p>}
      {media.operation && <p>{operationLabel[media.operation.action]} — slot {media.operation.slotId}</p>}
      <p>Video da completare: {media.queue.length}. Spazio temporaneo utilizzato: {gib(media.spoolBytes)} GiB su 20 GiB. Spazio libero sul server: {gib(media.freeBytes)} GiB.</p>
      <p className="p2p-help">Riserva libera sul disco: 8 GiB. Dei 20 GiB temporanei, 8 restano disponibili per preparare i video completi. La diretta ha precedenza; i trasferimenti interrotti riprendono dai dati già ricevuti.</p>
      {(media.spoolBytes >= 18 * 1024 ** 3 || media.freeBytes < 10 * 1024 ** 3) && <Alert variant="warning">Spazio in esaurimento: le nuove acquisizioni possono attendere. I video non ancora consegnati vengono conservati.</Alert>}
      {media.alerts.length > 0 && <Alert variant="warning">{media.alerts.length} operazioni richiedono attenzione dopo tentativi ripetuti. I file disponibili sono conservati. I recuperi non completabili restano in attesa di verifica.</Alert>}
      {[...new Set(media.alerts.map(a => a.reason))].filter(reason => mediaWarnings[reason]).map(reason => <Alert variant="warning" key={reason}>{mediaWarnings[reason]}</Alert>)}
    </section>}
    {p2p && <p>Gli slot già pianificati e quelli nuovi sono assegnati al P2P. Il ritorno online dell’NVR non cambia questa scelta.</p>}
    {p2p && status.nvrSuspension === 'pending' && <Alert variant="warning">Sospensione NVR in attesa di conferma. Al collegamento dovrà scartare i lavori locali di questo campo.</Alert>}
    {p2p && status.nvrSuspension === 'confirmed' && <p>Sospensione NVR confermata.</p>}
    {!p2p && status && status.generation > 0 && !status.nvrResumeConfirmed && <p>Ripresa dell’NVR in attesa di conferma.</p>}
    {error && <Alert variant="danger" role="alert">{error}</Alert>}
    {stale && <Alert variant="warning">La configurazione è cambiata. Ricarica il campo prima di eseguire un altro passaggio.</Alert>}
    {!p2p && saved.enabled && <>
      <Form.Group controlId={`p2p-worker-${fieldId}`}>
        <Form.Label>Server di registrazione P2P</Form.Label>
        <Form.Control as="select" value={worker} disabled={busy || stale} onChange={e => setWorker(e.target.value)}>
          <option value="">Seleziona il server</option>
          {agents.map(a => <option key={a.id} value={a.id} disabled={!a.ready}>{a.name}{a.ready ? '' : ' — non pronto'}</option>)}
        </Form.Control>
      </Form.Group>
      {!status.nvrPrepared && <p>Il controllo dei lavori dell’NVR deve essere preparato prima del primo passaggio.</p>}
      {selected && !prepared && <p>Il server deve completare la verifica delle impostazioni e degli XML di questo campo.</p>}
      {prepared && <p>Impostazioni e XML del campo ricevuti dal server P2P.</p>}
      <div className="p2p-field-actions"><Button type="button" disabled={busy || stale || !selected || !prepared || !status.nvrPrepared}
        onClick={() => change('p2p')}>{busy ? 'Passaggio in corso…' : 'Attiva P2P'}</Button></div>
      <p className="p2p-help mt-2">Si applica anche agli slot già pianificati e sospende soltanto questo campo sull’NVR. L’NVR può essere offline. Entrambe le configurazioni restano conservate; il campo mantiene calendario, telecamere e XML.</p>
    </>}
    {p2p && <div className="p2p-field-actions"><Button type="button" disabled={busy || stale} variant="outline-primary"
      onClick={() => change('nvr_local')}>{busy ? 'Passaggio in corso…' : 'Riattiva NVR locale'}</Button></div>}
  </div>;
}
